import * as pdfjs from 'pdfjs-dist';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  ViewEncapsulation,
  OnDestroy,
  ElementRef,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { SnAbilityService } from '@lib/ability';
import { SnMedia, SnWidget, SnWidgetMedia } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';

@Component({
  selector: 'sn-media-widget',
  templateUrl: './media-widget.component.html',
  styleUrls: ['./media-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MediaWidgetComponent implements OnInit, OnDestroy {
  get isImage(): boolean {
    if (!this.mediaItem) {
      return false;
    }
    const file = this.mediaItem?.description;
    return file?.includes('.jpeg') || file?.includes('.jpg') || file?.includes('.png');
  }

  get isPdf(): boolean {
    if (!this.mediaItem) {
      return false;
    }
    const file = this.mediaItem?.description;
    return file?.includes('.pdf');
  }

  @Input() public widget: SnWidget;
  @Input() public viewId: string;
  public pdfPage: number;
  public readonly canChange: boolean;
  public readonly canDelete: boolean;
  public readonly canView: boolean;
  public readonly form = new UntypedFormGroup({});
  public readonly formFields: SnFormFieldConfig[] = [
    {
      key: 'files',
      type: 'file-upload',
      templateOptions: {
        multiple: false,
        allowPreview: true,
      },
    },
  ];

  public formModel: any = {};
  public changeTitle: boolean = false;
  public changeMedia: boolean;
  public widgetMedia: SnWidgetMedia[];
  public mediaItem: SnMedia;
  public isLoading: boolean = false;
  public file: any = {};
  private readonly _destroy$ = new Subject<void>();
  private _canvas: HTMLCanvasElement;
  private _context: any;

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _ability: SnAbilityService,
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _elementRef: ElementRef,
  ) {
    this.canChange = this._ability.can({ subject: 'dashboards/widgetobject', action: 'change' });
    this.canDelete = this._ability.can({ subject: 'dashboards/widgetobject', action: 'delete' });
    this.canView = this._ability.can({ subject: 'dashboards/widget', action: 'view' });
  }

  public ngOnInit(): void {
    this._checkForMedia();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  public toggleMediaUpload(): void {
    this.changeMedia = !this.changeMedia;
    this._cd.detectChanges();
  }

  public download(): void {
    const downloadLink = document.createElement('a');
    downloadLink.setAttribute('download', this.mediaItem.description);
    downloadLink.setAttribute('href', this.mediaItem.file);
    downloadLink.click();
  }

  public remove(): void {
    if (!this.mediaItem) {
      return;
    }
    this._operationalViewsAPI
      .deleteViewWidgetMedia(this.viewId, this.widget.id, this.mediaItem.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        if (this.widgetMedia.length) {
          this.widgetMedia.forEach((media) => {
            if (this.mediaItem.id === media.id) {
              this.widgetMedia.splice(this.widgetMedia.indexOf(media), 1);
              this.mediaItem = this.widgetMedia[0];
              this._cd.detectChanges();
            }
          });
        }
      });
  }

  public select(item: SnWidgetMedia): void {
    this.mediaItem = item;
    this._cd.detectChanges();
    if (this.isPdf) {
      this._getDocument();
    }
  }

  public submitForm(): void {
    this.isLoading = true;
    const data = this.form.value;
    if (!this.changeTitle) {
      this._operationalViewsAPI
        .updateViewWidget(this.viewId, this.widget.id, this._transformToFormData(data))
        .pipe(takeUntil(this._destroy$))
        .subscribe((widget) => {
          this.widget.media = widget.media;
          this.changeMedia = false;
          this.widgetMedia = this.widget.media;
          this.mediaItem = this.widgetMedia[0];
          this.isLoading = false;
          this._cd.detectChanges();
          if (this.isPdf) {
            this._getDocument();
          }
        });
    } else {
      this.changeTitle = false;
      this._cd.detectChanges();
    }
    this.form.reset();
  }

  public nextPdfPage(): void {
    this.pdfPage++;
    this._loadPage();
  }

  public previousPdfPage(): void {
    this.pdfPage--;
    this._loadPage();
  }

  private _checkForMedia(): void {
    this.widgetMedia = this.widget.media;
    this.mediaItem = <SnMedia>this.widgetMedia[0];
    this._cd.detectChanges();
    if (this.isPdf) {
      this._getDocument();
    }
  }

  private _getDocument(): void {
    this.isLoading = true;
    this.pdfPage = 1;
    pdfjs.GlobalWorkerOptions.workerSrc = 'pdf.worker.js';
    const loadingTask = pdfjs.getDocument({
      url: this.mediaItem.file,
    });
    loadingTask.promise.then((file) => {
      this.file = file;
      this._loadPage();
    });
  }

  private _loadPage(): void {
    this.file.getPage(this.pdfPage).then((page) => {
      if (page) {
        page.cleanup();
      }
      const scale = 1;
      const viewport = page.getViewport({ scale });
      this._canvas = this._elementRef.nativeElement.querySelector('#canvasEl');
      this._context = this._canvas.getContext('2d');
      this._canvas.height = viewport.height;
      this._canvas.width = viewport.width;

      const renderContext = {
        canvasContext: this._context,
        viewport,
      };
      page.render(renderContext);
      page.cleanupAfterRender = true;
      this.isLoading = false;
      this._cd.detectChanges();
    });
  }

  private _transformToFormData(data: Object): FormData {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      switch (key) {
        case 'files':
          data[key].forEach((file) => {
            file.description = file.name;
            formData.append('files', file, file.name);
          });
          break;
        default:
          formData.append(key, data[key]);
      }
    });

    return formData;
  }
}
